import {
    CalendarIcon,
    CheckCircleIcon,
    ClockIcon,
    Cog6ToothIcon,
    EyeIcon,
    EyeSlashIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    ShieldCheckIcon,
    ShieldExclamationIcon,
    TrashIcon,
    TruckIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import AdminApiService from '../../utils/api';
import BrandsModelsSettings from './BrandsModelsSettings';

const StatusBadge = ({ status, isDeleted }) => {
  // Handle soft-deleted status
  if (isDeleted) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-red-50 text-red-800 border-red-200">
        Soft Deleted
      </span>
    );
  }
  
  const statusType = status || 'active';
  const styles = {
    active: 'bg-success-100 text-success-800 border-success-200',
    available: 'bg-success-100 text-success-800 border-success-200',
    inactive: 'bg-gray-100 text-gray-800 border-gray-200',
    sold: 'bg-secondary-100 text-secondary-800 border-secondary-200',
    pending: 'bg-warning-100 text-warning-800 border-warning-200',
    blocked: 'bg-danger-100 text-danger-800 border-danger-200'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${styles[statusType] || styles.active}`}>
      {statusType.charAt(0).toUpperCase() + statusType.slice(1)}
    </span>
  );
};

// New component for showing who added the car
const AddedByBadge = ({ car }) => {
  // Determine who added the car
  let addedBy = 'Unknown';
  let bgColor = 'bg-gray-100';
  let textColor = 'text-gray-800';
  
  if (car.seller?.role === 'agent') {
    addedBy = `Agent: ${car.seller.firstName} ${car.seller.lastName}`;
    bgColor = 'bg-blue-100';
    textColor = 'text-blue-800';
  } else if (car.seller?.sellerType === 'agent-created' && car.seller?.agentId) {
    addedBy = `Agent: ${car.seller.agentId.firstName} ${car.seller.agentId.lastName}`;
    bgColor = 'bg-purple-100';
    textColor = 'text-purple-800';
  } else if (car.seller) {
    addedBy = `Seller: ${car.seller.firstName || car.seller.businessName || 'Unknown'} ${car.seller.lastName || ''}`.trim();
    bgColor = 'bg-orange-100';
    textColor = 'text-orange-800';
  }
  
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
      {addedBy}
    </span>
  );
};

const VisibilityBadge = ({ visibility }) => {
  const visibilityType = visibility || 'public';
  const styles = {
    public: 'bg-success-100 text-success-800',
    private: 'bg-warning-100 text-warning-800',
    hidden: 'bg-gray-100 text-gray-800'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[visibilityType]}`}>
      {visibilityType === 'public' && <EyeIcon className="w-3 h-3 mr-1" />}
      {visibilityType === 'private' && <EyeSlashIcon className="w-3 h-3 mr-1" />}
      {visibilityType.charAt(0).toUpperCase() + visibilityType.slice(1)}
    </span>
  );
};

// Helper function to get card background color
const getCardBgColor = (car) => {
  // Soft-deleted cars - light red
  if (car.deletedAt) {
    return 'bg-red-50 border-red-200';
  }
  
  // Blocked cars - dark red
  if (car.status === 'blocked') {
    return 'bg-red-100 border-red-300';
  }
  
  // Pending cars - yellow
  if (car.status === 'pending') {
    return 'bg-yellow-50 border-yellow-200';
  }
  
  // Active/Available cars - green
  if (car.status === 'active' || car.status === 'available') {
    return 'bg-green-50 border-green-200';
  }
  
  // Default - white
  return 'bg-white border-gray-200';
};

const CarCard = ({ car, onAction }) => {
  const cardBgColor = getCardBgColor(car);
  const isDeleted = !!car.deletedAt;
  
  return (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -4, scale: 1.02 }}
    className={`${cardBgColor} rounded-xl overflow-hidden shadow-soft border hover:shadow-lg transition-all duration-300`}
  >
    <div className="relative">
      <img
        src={(car.images && car.images[0]?.url) || (car.images && car.images[0]) || 'https://via.placeholder.com/400x300'}
        alt={car.title || car.name}
        className="w-full h-48 object-cover"
      />
      <div className="absolute top-4 left-4 flex flex-col gap-2">
        <AddedByBadge car={car} />
        {isDeleted && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-600 text-white">
            🗑️ Deleted
          </span>
        )}
      </div>
      <div className="absolute top-4 right-4">
        <StatusBadge status={car.status || 'active'} isDeleted={isDeleted} />
      </div>
      {car.isApproved && !isDeleted && (
        <div className="absolute bottom-4 left-4">
          <div className="flex items-center space-x-1 bg-white bg-opacity-90 px-2 py-1 rounded-full">
            <CheckCircleIcon className="w-4 h-4 text-success-600" />
            <span className="text-xs font-medium text-success-600">Approved</span>
          </div>
        </div>
      )}
      <div className="absolute bottom-4 right-4 bg-white bg-opacity-90 px-3 py-1 rounded-full">
        <span className="text-sm font-bold text-gray-900">₹{((car.price || 0) / 100000).toFixed(1)}L</span>
      </div>
    </div>

    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{car.title || car.name || 'Untitled Car'}</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <MapPinIcon className="w-4 h-4" />
            <span>
              {typeof car.location === 'object' && car.location 
                ? `${car.location.city || ''}, ${car.location.state || ''}`.replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Not specified'
                : car.location || 'Not specified'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-lg font-bold text-gray-900">{car.year || 'N/A'}</div>
          <div className="text-xs text-gray-600">Year</div>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-lg font-bold text-gray-900">{car.kmDriven != null ? Number(car.kmDriven).toLocaleString() : 'N/A'}</div>
          <div className="text-xs text-gray-600">KM</div>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-lg font-bold text-gray-900">{car.fuelType}</div>
          <div className="text-xs text-gray-600">Fuel</div>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-lg font-bold text-gray-900">{car.transmission || 'N/A'}</div>
          <div className="text-xs text-gray-600">Transmission</div>
        </div>
      </div>

      {(car.seller || car.agent) && (
        <div className="mb-4 p-3 bg-primary-50 rounded-lg">
          <div className="flex items-center justify-between">
            {car.seller && (
              <div>
                <div className="text-sm font-medium text-gray-900">Seller</div>
                <div className="text-sm text-gray-600">
                  {car.seller.firstName && car.seller.lastName 
                    ? `${car.seller.firstName} ${car.seller.lastName}` 
                    : car.seller.name || 'N/A'}
                </div>
              </div>
            )}
            {car.agent && (
              <div>
                <div className="text-sm font-medium text-gray-900">Agent</div>
                <div className="text-sm text-gray-600">
                  {car.agent.firstName && car.agent.lastName 
                    ? `${car.agent.firstName} ${car.agent.lastName}` 
                    : car.agent.name || 'N/A'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-4 text-center">
        <div>
          <div className="text-lg font-bold text-primary-600">{car.views || 0}</div>
          <div className="text-xs text-gray-600">Views</div>
        </div>
        <div>
          <div className="text-lg font-bold text-secondary-600">{car.inquiryCustomers?.length || 0}</div>
          <div className="text-xs text-gray-600">Inquiries</div>
        </div>
        <div>
          <div className="text-lg font-bold text-success-600">{car.testRideCustomers?.length || 0}</div>
          <div className="text-xs text-gray-600">Test Drives</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <CalendarIcon className="w-4 h-4" />
          <span>Listed {new Date(car.createdAt || car.listingDate).toLocaleDateString()}</span>
          {isDeleted && car.deletedAt && (
            <span className="text-red-600 ml-2">
              | Deleted {new Date(car.deletedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to={`/cars/${car._id || car.id}`}
            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="View Details"
          >
            <EyeIcon className="w-4 h-4" />
          </Link>
          {!isDeleted && (
            <button
              onClick={() => onAction(car.status === 'blocked' ? 'unblock' : 'block', car)}
              className={`p-2 rounded-lg transition-colors ${
                car.status === 'blocked'
                  ? 'text-gray-400 hover:text-success-600 hover:bg-success-50'
                  : 'text-gray-400 hover:text-warning-600 hover:bg-warning-50'
              }`}
              title={car.status === 'blocked' ? 'Unblock Car' : 'Block Car'}
            >
              {car.status === 'blocked' ? <ShieldCheckIcon className="w-4 h-4" /> : <ShieldExclamationIcon className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={() => onAction(isDeleted ? 'permanent-delete' : 'delete', car)}
            className="p-2 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
            title={isDeleted ? 'Permanently Delete Car' : 'Delete Car'}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </motion.div>
);
};

const CarsManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [sortBy, setSortBy] = useState('listingDate');
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBrandSettings, setShowBrandSettings] = useState(false);

  useEffect(() => {
    fetchCars();
  }, []);

  const fetchCars = async () => {
    setLoading(true);
    try {
      const response = await AdminApiService.getCars();
      const carsData = response.data || response;
      setCars(Array.isArray(carsData) ? carsData : []);
    } catch (error) {
      console.error('Failed to fetch cars:', error);
      toast.error('Failed to load cars data');
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedCars = useMemo(() => {
    const normalizedSearch = String(searchTerm || '').trim().toLowerCase();

    return cars
      .filter(car => {
        const locationStr = typeof car.location === 'object' && car.location
          ? `${car.location.city || ''} ${car.location.state || ''}`.toLowerCase()
          : (car.location || '').toLowerCase();
        const sellerName = `${car.seller?.firstName || ''} ${car.seller?.lastName || ''}`.trim().toLowerCase();
        const sellerEmail = String(car.seller?.email || '').toLowerCase();
        const carId = String(car._id || car.id || '').toLowerCase();
        
        const matchesSearch = !normalizedSearch ||
                            (car.title || car.name || '').toLowerCase().includes(normalizedSearch) ||
                            (car.brand || '').toLowerCase().includes(normalizedSearch) ||
                            (car.model || '').toLowerCase().includes(normalizedSearch) ||
                            locationStr.includes(normalizedSearch) ||
                            sellerName.includes(normalizedSearch) ||
                            sellerEmail.includes(normalizedSearch) ||
                            carId.includes(normalizedSearch);
        
        // Handle status filter including soft-deleted
        let matchesStatus = true;
        if (statusFilter === 'deleted') {
          matchesStatus = !!car.deletedAt;
        } else if (statusFilter !== 'all') {
          matchesStatus = car.status === statusFilter && !car.deletedAt;
        }
        
        const matchesVisibility = visibilityFilter === 'all' || car.visibility === visibilityFilter;
        const matchesBrand = brandFilter === 'all' || car.brand === brandFilter;
        return matchesSearch && matchesStatus && matchesVisibility && matchesBrand;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'price':
            return (b.price || 0) - (a.price || 0);
          case 'views':
            return (b.views || 0) - (a.views || 0);
          case 'listingDate':
            return new Date(b.createdAt || b.listingDate) - new Date(a.createdAt || a.listingDate);
          case 'title':
            return (a.title || a.name || '').localeCompare(b.title || b.name || '');
          default:
            return 0;
        }
      });
  }, [searchTerm, statusFilter, visibilityFilter, brandFilter, sortBy, cars]);

  const handleAction = async (action, car) => {
    const carTitle = car.title || car.name || 'this car';

    try {
      switch (action) {
        case 'block':
          if (!window.confirm(`Are you sure you want to block "${carTitle}"? It will be hidden from all users.`)) {
            return;
          }
          await AdminApiService.blockCar(car._id);
          toast.success(`${carTitle} has been blocked`);
          fetchCars();
          break;

        case 'unblock':
          await AdminApiService.unblockCar(car._id);
          toast.success(`${carTitle} has been unblocked`);
          fetchCars();
          break;

        case 'permanent-delete':
          if (!window.confirm(`⚠️ PERMANENT DELETE\n\nAre you sure you want to PERMANENTLY delete "${carTitle}"?\n\nThis will remove the car completely from the database. This action CANNOT be undone!`)) {
            return;
          }
          await AdminApiService.deleteCar(car._id);
          toast.success(`${carTitle} has been permanently deleted`);
          fetchCars();
          break;

        case 'delete':
          if (!window.confirm(`Are you sure you want to delete "${carTitle}"? This will soft-delete the car (can be recovered).`)) {
            return;
          }
          await AdminApiService.deleteCar(car._id);
          toast.success(`${carTitle} has been deleted`);
          fetchCars();
          break;

        default:
          break;
      }
    } catch (error) {
      console.error(`Failed to ${action} car:`, error);
      toast.error(error.response?.data?.message || `Failed to ${action} car`);
    }
  };

  const stats = {
    total: cars.length,
    active: cars.filter(c => (c.status === 'active' || c.status === 'available') && !c.deletedAt).length,
    pending: cars.filter(c => c.status === 'pending' && !c.deletedAt).length,
    deleted: cars.filter(c => !!c.deletedAt).length,
    blocked: cars.filter(c => c.status === 'blocked' && !c.deletedAt).length,
    totalValue: cars.reduce((sum, c) => sum + (c.price || 0), 0)
  };

  const uniqueBrands = [...new Set(cars.map(car => car.brand).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <h1 className="text-3xl font-display font-bold text-gray-900">
            Cars Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage all car listings and monitor performance
          </p>
        </motion.div>
        <motion.button
          type="button"
          onClick={() => setShowBrandSettings(true)}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 hover:text-primary-600 hover:border-primary-200"
          title="Brand & model settings"
        >
          <Cog6ToothIcon className="w-5 h-5" />
          <span className="font-medium">Brand &amp; Model Settings</span>
        </motion.button>
      </div>

      <BrandsModelsSettings open={showBrandSettings} onClose={() => setShowBrandSettings(false)} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Cars</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="p-4 bg-primary-100 rounded-xl">
              <TruckIcon className="w-8 h-8 text-primary-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Listings</p>
              <p className="text-3xl font-bold text-success-600 mt-2">{stats.active}</p>
            </div>
            <div className="p-4 bg-success-100 rounded-xl">
              <CheckCircleIcon className="w-8 h-8 text-success-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Approval</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending}</p>
            </div>
            <div className="p-4 bg-yellow-100 rounded-xl">
              <ClockIcon className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Soft Deleted</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{stats.deleted}</p>
            </div>
            <div className="p-4 bg-red-100 rounded-xl">
              <TrashIcon className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search cars..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-64"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="available">Available</option>
              <option value="pending">Pending</option>
              <option value="blocked">Blocked</option>
              <option value="deleted">Soft Deleted</option>
              <option value="inactive">Inactive</option>
              <option value="sold">Sold</option>
            </select>
            
            <select
              value={visibilityFilter}
              onChange={(e) => setVisibilityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Visibility</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="hidden">Hidden</option>
            </select>

            <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Brands</option>
              {uniqueBrands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="listingDate">Sort by Date</option>
              <option value="price">Sort by Price</option>
              <option value="views">Sort by Views</option>
              <option value="title">Sort by Title</option>
            </select>
            <span className="text-sm text-gray-500">
              {filteredAndSortedCars.length} of {cars.length} cars
            </span>
          </div>
        </div>
      </motion.div>

      {/* Cars Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredAndSortedCars.map((car, index) => (
          <motion.div
            key={car.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <CarCard car={car} onAction={handleAction} />
          </motion.div>
        ))}
      </div>

      {filteredAndSortedCars.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <TruckIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No cars found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria</p>
        </motion.div>
      )}
    </div>
  );
};

export default CarsManagement;